<template>
    <div>
        <div class="help-block">{{$t("Sisesta kaastundeavalduse sisu")}}</div>
        <div class="row">
            <div class="col-md-6" ref="dataContainer">
                <div class="form-group">
                    <label for="name">
                        {{$t('Lahkunu nimi')}}
                        <span class="fas fa-info-circle" v-b-popover.hover.bottom="$t('popover lahkunu nimi')"></span>
                    </label>
                    <input :maxlength="200" @change="saveState" class="form-control" id="name" v-model="name">
                </div>
                <div class="form-group">
                    <label>
                        {{$t('Sunnikuupaev')}}
                        <span class="fas fa-info-circle" v-b-popover.hover.bottom="$t('popover synniaeg')"></span>
                    </label>
                    
                    <VueDatePicker 
                        :monday-first="true"
                        :start-date="new Date('1950-01-01')" 
                        @change="saveState" 
                        :clear-button="true"
                        clear-button-icon="fa fa-times"
                        :locale="store.datepickerLocale"
                        :formats="{ input: 'dd.MM.yyyy' }"
                        input-class="form-control" 
                        :time-config="{ enableTimePicker: false }"
                        :action-row="{ showSelect: false, showCancel: false, showPreview: false }"
                        auto-apply
                        :flow="{ steps: ['year', 'month', 'calendar']}"
                        v-model="bornDate"
                    >
                    </VueDatePicker>
                </div>
                <div class="form-group">
                    <label>
                        {{$t('Lahkumiskuupaev')}}
                        <span class="fas fa-info-circle" v-b-popover.hover.bottom="$t('popover lahkumisaeg')"></span>
                    </label>
                    <VueDatePicker 
                        :monday-first="true"
                        @change="saveState" 
                        :clear-button="true" 
                        :locale="store.datepickerLocale"
                        clear-button-icon="fa fa-times"
                        :formats="{ input: 'dd.MM.yyyy' }"
                        :time-config="{ enableTimePicker: false }"
                        :action-row="{ showSelect: false, showCancel: false, showPreview: false }"
                        auto-apply
                        input-class="form-control"
                        v-model="deathDate"
                    >
                    </VueDatePicker>
                </div>
                <div class="form-group">
                    <label class="w-100" for="beginningText">{{$t('Alguse tekst')}}
                        <span class="fas fa-info-circle"
                              v-b-popover.hover.bottom="$t('popover kaastunne algusetekst')"></span>
                        <span class="float-right charcount">{{(200 - beginningText.length)}}</span>
                    </label>

                    <textarea :maxlength="200" @change="saveState" class="form-control" id="beginningText"
                              rows="9" v-model="beginningText">
                    </textarea>
                </div>

                <div class="form-group">
                    <label class="w-100" for="endingText">{{$t('Lopu tekst')}}
                        <span class="fas fa-info-circle"
                              v-b-popover.hover.bottom="$t('popover kaastunne loputekst')"></span>
                        <span class="float-right charcount">{{(200 - endingText.length)}}</span>
                    </label>
                    <textarea :maxlength="200" @change="saveState" class="form-control" id="endingText"
                              rows="9" v-model="endingText">
                    </textarea>
                </div>
                <div class="form-group">
                    <label for="name w-100">
                        {{$t('Arasaatmine')}}
                        <span class="fas fa-info-circle" v-b-popover.hover.bottom="$t('popover arasaatmine')"></span>
                    </label>
                    <input :maxlength="200" @change="saveState" class="form-control" id="funeral" v-model="funeral">
                </div>

                <div class="form-group custom-control custom-checkbox vertical-margin">
                    <input @change="saveState" class="custom-control-input" id="has-poem" type="checkbox"
                           v-model="hasPoem">
                    <label class="custom-control-label w-100" for="has-poem">{{$t('Luuletus')}}
                        <span class="fas fa-info-circle"
                              v-b-popover.hover.bottom="$t('popover kaastunne luuletus')"></span>
                        <span class="float-right charcount" v-if="customPoem">{{(500 - customPoem.length)}}</span>
                    </label>
                </div>
                <div v-if="hasPoem">
                    <textarea :maxlength="500" @change="saveState" class="form-control mb-2"
                              id="customPoem" rows="9" v-model="customPoem">
                    </textarea>
                    <v-select :options="poems[$i18n.locale]" :placeholder="$t('Vali olemasolev luuletus')"
                              :searchable="false" @input="saveState"
                              v-model="poem">
                        <template v-slot:option="option">
                            <span v-html="option.text"></span>
                        </template>
                    </v-select>
                </div>

                <div class="form-group custom-control custom-checkbox vertical-margin">
                    <input @change="saveState" @click="symbolChanged()" class="custom-control-input" id="has-symbol"
                           type="checkbox" v-model="imageOrSymbol" value="symbol">
                    <label class="custom-control-label w-100" for="has-symbol">
                        {{$t('Pilt voi sumbol')}}
                         <span class="fas fa-info-circle"
                          v-b-popover.hover.bottom="$t('popover kaastunne pilt symbol')"></span>
                    </label>
                   
                </div>
                <div class="form-group custom-control custom-checkbox" v-if="imageOrSymbol">
                    <div class="row">
                        <div class="col-2 m-0 p-1" v-for="s in symbols">
                            <img :src="s.data" @click="pickSymbol(s)" class="w-100 border"
                                 v-bind:class="{'border-dark':symbol!=null && symbol.id === s.id}">
                        </div>
                    </div>
                </div>
                <div v-if="imageOrSymbol">
                    <div class="form-group">
                        <input 
                            ref="fileInput"
                            type="file" 
                            accept="image/*" 
                            @change="onFileChosen" 
                            class="form-control d-none" 
                        />
                        <input :value="$t('Lae pilt oma arvutist')" @click="pickImage()"
                               class="form-control"
                               readonly/>
                    </div>
                </div>
                <MyCropper
                    :visible="Boolean(imageOrSymbol && imageContent)"
                    :image-content="imageContent"
                    :callback="cropperCallback"
                />
            </div>
            <div :class="previewTabClasses">
                <div class='box' :class="previewClasses">
                    <div class='content preview preview-box'>
                        <div>
                            <div class="text-center poem p-1" v-if="hasPoem && !imageOrSymbol">
                                <span v-html="poem.text" v-if="poem"></span>
                                <span style="white-space: pre-line" v-else>{{customPoem}}</span>
                            </div>
                            <div v-else-if="!hasPoem && imageOrSymbol">
                                <div :height="previewSize" :width="previewSize" v-if="hasImage">
                                    <img :src="imgUrl" class="w-100">
                                </div>
                                <div class="text-center" v-if="hasSymbol && symbol!=null">
                                    <img :src="symbol.data" :width="previewSize">
                                </div>
                            </div>
                            <div v-if="hasPoem && imageOrSymbol">
                                <div class="col p-1">
                                    <div class="row">
                                        <div class="col-md-6">
                                            <div v-if="hasImage && previewSize > 0">
                                                <img :height="previewSize" :src="imgUrl" :width="previewSize">
                                            </div>
                                            <div :height="previewSize" :width="previewSize" class="text-center"
                                                 v-if="hasSymbol && symbol!=null">
                                                <img :src="symbol.data" class="w-100">
                                            </div>
                                        </div>
                                        <div class="col-md-6 p-1 text-left poem">
                                            <span v-html="poem.text" v-if="poem"></span>
                                            <span style="white-space: pre-line" v-else>{{customPoem}}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div>
                            <div class="text-center">{{beginningText}}</div>
                            <h3 class="text-center">{{name}}</h3>
                            <h3 class="text-center datums-text">
                                {{formatDate(bornDate)}}&nbsp;<span v-if="bornDate || deathDate">&#8209;</span>&nbsp;{{formatDate(deathDate)}}
                            </h3>
                            <div class="text-center">{{endingText}}</div>
                            <div class="text-center">{{funeral}}</div>
                        </div>
                    </div>
                </div>
                <br>
                <single-price v-if="!adid"></single-price>
            </div>
        </div>
        <button class="btn btn-primary" type="button" v-if="adid" v-on:click.stop="saveState('save')">
            Salvesta leinakuulutus
        </button>
        <div v-else>
            <div class="col-md-12 p-0 text-center" v-html="$t('erisoovid')"></div>
            <a @click="onContinue" class="btn btn-primary btn-block"
                v-bind:class="{disabled: !formReady || store.isLoading}">
                <MoveForwardButton />
            </a>
        </div>
    </div>
</template>

<script setup>
import MoveForwardButton from './MoveForwardButton.vue';
import { useMainStore } from '../includes/store';
import moment from 'moment';
import { onBeforeUnmount, onDeactivated, onMounted, watch, computed, ref } from 'vue';
import vSelect from 'vue-select';
import singlePrice from './SinglePriceInfo';
import { toast } from 'vue3-toastify';
import MyCropper from './MyCropper.vue';

const store = useMainStore();

const props = defineProps({
    adid: { type: [String, Number], required: false },
    formReady: { type: Boolean, required: false }
});

const dataContainer = ref(null);
const fileInput = ref(null);
const name = ref("");
const funeral = ref("");
const bornDate = ref(null);
const deathDate = ref(null);
const beginningText = ref("");
const endingText = ref("");
const hasPoem = ref(false);
const hasSymbol = ref(false);
const hasImage = ref(false);
const imageOrSymbol = ref(false);
const poems = ref([]);
const poem = ref(null);
const customPoem = ref("");
const symbols = ref([]);
const symbol = ref(null);
const imageContent = ref(null);
const imgUrl = ref(null);
const originalImage = ref(null);
const saveTimeout = ref(null);
const isMounted = ref(false);

onMounted(() => {
    loadPoems();
    loadSymbols();
    loadState();
    isMounted.value = true;
});

onDeactivated(() => {
    saveState();
});

onBeforeUnmount(() => {
    saveState();
});

const formatDate = function (value) {
    if (value) {
        let date = moment(value);
        let m = date.month();
        let month = "I";
        switch (m) {
            case 0:
                month = "I";
                break;
            case 1:
                month = "II";
                break;
            case 2:
                month = "III";
                break;
            case 3:
                month = "IV";
                break;
            case 4:
                month = "V";
                break;
            case 5:
                month = "VI";
                break;
            case 6:
                month = "VII";
                break;
            case 7:
                month = "VIII";
                break;
            case 8:
                month = "IX";
                break;
            case 9:
                month = "X";
                break;
            case 10:
                month = "XI";
                break;
            default:
                month = "XII";
        }
        let text = date.format('D. [roman_month] YYYY');
        text = text.replace("roman_month", month);
        return text;
    }
}

const loadPoems = () => {
    axios.get('/api/poems?type=condolence').then(response => {
        poems.value = response.data;
    })
}
const loadSymbols = () => {
    axios.get('/api/symbols?type=condolence').then(response => {
        symbols.value = response.data;
    })
}
const pickImage = () => {
    hasImage.value = true;
    hasSymbol.value = false;
    symbol.value = null;
    if (fileInput.value) {
        fileInput.value.click();
    }
}

const pickSymbol = (selectedSymbol) => {
    hasImage.value = false;
    hasSymbol.value = true;
    symbol.value = selectedSymbol;
    saveState();
}

const loadState = () => {
    if (props.adid) {
        axios.get('/admin/advertisement/' + props.adid + '/data').then(response => {
            setState(response.data);
        })
    } else {
        let condolence = store.stepInfo.content.condolence;
        setState(condolence);
    }
}

const setState = (condolence) => {
    if (condolence.beginningText == null) {
        condolence.beginningText = "";
    }
    if (condolence.endingText == null) {
        condolence.endingText = "";
    }
    bornDate.value = condolence.bornDate;
    deathDate.value = condolence.deathDate;
    name.value = condolence.name;
    funeral.value = condolence.funeral;
    beginningText.value = condolence.beginningText;
    endingText.value = condolence.endingText;
    hasPoem.value = condolence.hasPoem;
    hasSymbol.value = condolence.hasSymbol;
    hasImage.value = condolence.hasImage;
    imageOrSymbol.value = condolence.imageOrSymbol;
    poem.value = condolence.poem;
    customPoem.value = condolence.customPoem;
    symbol.value = condolence.symbol;
    imgUrl.value = condolence.imgUrl;
    originalImage.value = condolence.originalImage;
    imageContent.value = condolence.originalImage;
}
const saveState = async (saveNow, disableTimeout = false) => {
    console.log("Saving condolence state");
    let postData = {
        bornDate: bornDate.value,
        deathDate: deathDate.value,
        name: name.value,
        funeral: funeral.value,
        beginningText: beginningText.value,
        endingText: endingText.value,
        hasPoem: hasPoem.value === true,
        hasSymbol: hasSymbol.value === true,
        hasImage: hasImage.value === true,
        imageOrSymbol: imageOrSymbol.value === true,
        poem: poem.value,
        customPoem: customPoem.value,
        symbol: symbol.value,
        imgUrl: imgUrl.value,
        originalImage: originalImage.value
    };

    if (saveTimeout.value) {
        clearTimeout(saveTimeout.value);
    }

    if (props.adid) {
        if (saveNow === "save") {
            axios.put('/admin/advertisement/' + props.adid + '/condolence', postData).then(_ => {
                toast.info('Kuulutus salvestatud');
            }).catch(errors => {
                if (errors.response.status === 422) {
                    for (const errName in errors.response.data.errors) {
                        toast.error(errors.response.data.errors[errName][0]);
                    }
                } else {
                    toast.error('Salvestamine ebaõnnestus');
                }
            });
        } else {
            console.log('not saving data');
        }
    } else {
        store.setCondolence(postData);

        if (disableTimeout) {
            store.setIsLoading(true);
            await axios.post('/state/condolence', {
                state: postData
            }).catch(_ => {
                store.sessExpired();
            }).finally(() => {
                store.setIsLoading(false);
            });
        } else {
            saveTimeout.value = setTimeout(_ => {
                axios.post('/state/condolence', {
                    state: postData
                }).catch(_ => {
                    store.sessExpired();
                });
            }, 2500);
        }
    }
}

const symbolChanged = (event) => {
    if (!imageOrSymbol.value) {
        hasImage.value = false;
        hasSymbol.value = false;
    }
}

const cropperCallback = (dataUrl, originalDataUrl) => {
    imgUrl.value = dataUrl;
    imageContent.value = originalDataUrl;
    originalImage.value = originalDataUrl;
    saveState();
}

function fileToDataURL(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => {
      reader.abort()
      reject(new Error('Failed to read file'))
    }
    reader.onload = () => resolve(reader.result) // result is a data:... base64 string
    reader.readAsDataURL(file)
  })
}

const onFileChosen = async (event) => {
    const file = event.target.files && event.target.files[0]
    if (!file) return

    try {
        // convert to data URL (base64)
        const dataUrl = await fileToDataURL(file)
        imageContent.value = dataUrl
        event.target.value = '';
    } catch (err) {
        console.error('Could not read file as data URL', err)
    }
};
       
const previewSize = computed(() => {
    if (!isMounted.value) {
        return 2;
    }
    let previewDiv = dataContainer.value;

    return previewDiv.clientWidth / 2 * 0.85;
})

const previewClasses = computed(() => {
    let newPreview = {
        "box-half": !(imageOrSymbol.value && hasPoem.value)
    };

    return newPreview;
})

const previewTabClasses = computed(() => {
    return {
        "offset-md-1 col-md-5": !(imageOrSymbol.value && hasPoem.value),
        "col-md-6": imageOrSymbol.value && hasPoem.value
    };
})

const onContinue = async () => {
    await saveState("save", true);
    store.changeStep('dates');
}

watch(() => store.stateLoaded, (loaded) => {
    if (loaded) {
        loadState();
    }
}, { immediate: true });

</script>

<style scoped>

    .preview {
        padding: 5px;
        border: solid 4px;
    }

    .datums-text {
        font-size: 14px;
    }

    .adborder {
        border: solid 4px;
    }

    .adbackground {
        background: darkgrey;
    }

    .adimage {
        border: dotted 1px;
    }
</style>
